import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { X, Loader2, Check, AlertCircle, Plus, Truck } from 'lucide-react';

interface EditTrackingsModalProps {
  purchaseId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface Tracking {
  tracking_number: string;
  carrier: string;
}

interface FetchedTracking {
  index: number;
  tracking_number: string;
  carrier: string | null;
}

interface TrackingsData {
  purchase: { id: number; link_source: string } | null;
  trackings: FetchedTracking[];
}

export function EditTrackingsModal({ purchaseId, onClose, onSuccess }: EditTrackingsModalProps) {
  const [trackings, setTrackings] = useState<Tracking[]>([{ tracking_number: '', carrier: '' }]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await api.get<TrackingsData>(
        `/amazon/purchases/${purchaseId}/trackings`
      );
      if (cancelled) return;
      if (!res.success) {
        setError(res.error || 'No se pudieron cargar los trackings');
        setLoading(false);
        return;
      }
      const fetched: Tracking[] = (res.data?.trackings || []).map((t: FetchedTracking) => ({
        tracking_number: t.tracking_number || '',
        carrier: t.carrier || '',
      }));
      setTrackings(fetched.length > 0 ? fetched : [{ tracking_number: '', carrier: '' }]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [purchaseId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const clean = trackings
      .filter((t) => t.tracking_number.trim().length > 0)
      .map((t) => ({
        tracking_number: t.tracking_number.trim(),
        carrier: t.carrier.trim() || null,
      }));
    const res = await api.put<TrackingsData>(
      `/amazon/purchases/${purchaseId}/trackings`,
      { trackings: clean }
    );
    if (res.success) {
      onSuccess();
    } else {
      setError(res.error || 'Error al guardar');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Truck size={18} className="text-[var(--color-primary)]" />
            <h3 className="font-bold text-[var(--color-text)]">
              Editar trackings — Compra #{purchaseId}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"
          >
            <X size={18} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-[var(--color-text-secondary)]">
              <Loader2 size={20} className="animate-spin mr-2" /> Cargando…
            </div>
          ) : (
            <>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Agregá uno o más números de seguimiento. El primero queda como principal.
                Todos se pueden escanear en el depósito.
              </p>
              <div className="space-y-1.5">
                {trackings.map((t, idx) => (
                  <div key={idx} className="flex gap-1.5 items-center">
                    <input
                      type="text"
                      value={t.tracking_number}
                      onChange={(e) => {
                        const next = [...trackings];
                        next[idx] = { ...next[idx], tracking_number: e.target.value };
                        setTrackings(next);
                      }}
                      placeholder={idx === 0 ? 'Tracking principal' : 'Tracking del paquete extra'}
                      className="flex-1 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm font-mono text-[var(--color-text)]"
                    />
                    <input
                      type="text"
                      value={t.carrier}
                      onChange={(e) => {
                        const next = [...trackings];
                        next[idx] = { ...next[idx], carrier: e.target.value };
                        setTrackings(next);
                      }}
                      placeholder="Empresa"
                      className="w-32 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]"
                    />
                    {trackings.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setTrackings(trackings.filter((_, i) => i !== idx))}
                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Quitar este tracking"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setTrackings([...trackings, { tracking_number: '', carrier: '' }])
                  }
                  className="text-xs text-[var(--color-primary)] hover:underline flex items-center gap-1 mt-1"
                >
                  <Plus size={12} /> Agregar otro tracking
                </button>
              </div>
            </>
          )}

          {error && (
            <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm">
              <AlertCircle size={14} className="text-red-500" />
              <span className="text-red-700 dark:text-red-400">{error}</span>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm border border-[var(--color-border)] rounded hover:bg-[var(--color-bg-tertiary)] text-[var(--color-text)]"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-3 py-1.5 text-sm bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded disabled:opacity-50 flex items-center gap-1"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
